<template>
  <ApiErrorDisplay v-if="apiError" v-bind="apiError" />

  <!-- Registration is a fully client-rendered surface: there is no server-driven
       display page for /register, and the flow (strength meter, debounced
       Companies-House typeahead, dissolved warnings) is richer than the generic
       Form component. Rendered inside the path-classed wrapper so the SCSS
       split-panel bridge still applies. -->
  <div v-else-if="isRegisterRoute" :class="{ [getPathClass()]: true }" class="wrapper h-100">
    <Messages />
    <RegistrationFlow />
  </div>

  <!-- Invite acceptance is a public, token-gated client-rendered surface (like
       /register): the invitee sets their own password and is logged in. -->
  <div v-else-if="isInviteRoute" :class="{ [getPathClass()]: true }" class="wrapper h-100">
    <Messages />
    <InviteAccept />
  </div>

  <!-- Everything else: the normal server-driven page. On auth routes we prepend
       the marketing brand panel (real DOM) so the SCSS turns it into the mockup's
       split panel — the form on the right stays server-driven. -->
  <div
    v-else
    :class="{ [getPathClass()]: true, loading }"
    class="wrapper h-100"
  >
    <Messages />
    <loading-spinner v-if="loading" />
    <build-number v-if="buildNumber" :build-number="buildNumber" />
    <!-- Send safety slice 2: the workspace's email sending state. -->
    <WorkspaceSendingBanner v-if="isWorkspaceRoute" />

    <AuthBrandPanel v-if="isAuthRoute" v-bind="authPanel" />

    <component
      :is="component.name"
      v-for="(component) in pageComponents"
      :key="component.id"
      :classes="component.classes"
      :componentData="component"
      :componentId="component.id"
      :components="component.components"
      v-bind="component.props" />
  </div>
</template>

<script>
import { useRoute } from "vue-router";
import { ref, watch, computed } from "vue";
import { getPathClass } from "@apexl/icarus-core/src/mixins/classFunctions";
import useLoadingStore from "@apexl/icarus-core/src/store/useLoadingStore";
import { storeToRefs } from "pinia";
import Messages from "@apexl/icarus-core/src/components/messages/Messages.vue";
import LoadingSpinner from "@apexl/icarus-core/src/components/partials/LoadingSpinner.vue";
import useApiError from "@apexl/icarus-core/src/store/useApiErrorStore";
import ApiErrorDisplay from "@apexl/icarus-core/src/components/ApiErrorDisplay.vue";
import BuildNumber from "@apexl/icarus-core/src/components/BuildNumber.vue";
import usePageGetter from "@apexl/icarus-core/src/services/usePageGetter";
import AuthBrandPanel from "@root/components/ui/AuthBrandPanel.vue";
import RegistrationFlow from "@root/components/RegistrationFlow.vue";
import InviteAccept from "@root/components/InviteAccept.vue";
import WorkspaceSendingBanner from "@root/components/WorkspaceSendingBanner.vue";

// Onboarding slice 2a — project fork of icarus-core's Icarus.vue.
// Adds two auth-surface behaviours over the vendor component:
//   1. the dark marketing brand panel on login / forgot / reset routes, and
//   2. the client-rendered registration flow on /register(/company).
// Everything else is a faithful copy of the vendor render loop.
const AUTH_PATHS = ['/login', '/forgot-password', '/reset-password', '/reset-password-success'];
// The signed-in app sections, where the sending banner is shown.
const WORKSPACE_SECTIONS = ['/admin', '/platform', '/outreach', '/acquire', '/analytics'];

export default {
  name: 'Icarus',
  components: { BuildNumber, ApiErrorDisplay, LoadingSpinner, Messages, AuthBrandPanel, RegistrationFlow, InviteAccept, WorkspaceSendingBanner },
  setup() {
    // noinspection JSUnresolvedReference - this is injected by webpack
    const buildNumber = BUILD_NUMBER;
    const { setLoading } = useLoadingStore();
    const { loading } = storeToRefs(useLoadingStore());
    const { apiError } = storeToRefs(useApiError());

    const { getPage } = usePageGetter();
    const route = useRoute();

    const pageComponents = ref([]);
    const pageMetaData = ref({
      title: "IO Dashboard"
    });

    const isRegisterRoute = computed(() => {
      const p = route.path.replace(/\/+$/, '') || '/';
      return p === '/register' || p === '/register/company';
    });
    const isAuthRoute = computed(() => AUTH_PATHS.includes(route.path.replace(/\/+$/, '') || '/'));
    const isInviteRoute = computed(() => /^\/invite\/[^/]+/.test(route.path));
    const isWorkspaceRoute = computed(() => WORKSPACE_SECTIONS.includes('/' + (route.path.split('/')[1] || '')));

    // Static marketing copy for the login/forgot/reset panel — NO pre-auth data.
    const authPanel = computed(() => ({
      kick: 'Welcome back',
      heading: "Your pipelines keep <em>working while you're away</em>.",
      sub: 'Sign in to see what your pipelines found, who opened, and who replied.',
      foot: 'Made in the Cotswolds · Companies House native',
    }));

    async function _getPage() {
      let url = route.path;
      const query = route.query;
      const queryString = Object
        .entries(query)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      if (queryString) {
        url = url + '?' + queryString;
      }

      const data = await getPage(url)

      if (data && data.components) {
        pageComponents.value = data.components.filter(component => !!component);
        pageMetaData.value = data?.metadata;
      }

      // Return the raw fetch result so the caller can tell a real page (truthy)
      // from a page-tree MISS (usePageGetter returns null on a 404/error — only
      // a 403 rethrows). A missing page under a VALID prefix (e.g.
      // /admin/nonexistent) is exactly what the index.js first-segment guard
      // can't catch, so we surface it here.
      return data;
    }

    async function pageLoad() {
      // Registration has no server page — never fetch it (avoids a spurious 404
      // and a stuck loading spinner). The template renders RegistrationFlow.
      if (isRegisterRoute.value || isInviteRoute.value) {
        setLoading(false);
        return;
      }
      setLoading(true);
      //check we can access this route.
      try {
        const data = await _getPage();
        // The page-tree fetch RESOLVED but there is no page here — an unknown
        // path under an otherwise-valid prefix (the index.js catch-all only
        // checks the first segment, so /admin/nonexistent slips through to here
        // and would otherwise strand the user on an empty shell / "Loading…").
        // Do the SAME full-load to the branded static /404.html the guard does.
        // Auth routes are server-driven but always exist — never 404 them; a
        // 403 (the only status usePageGetter rethrows) is handled in catch.
        if (!data && !isAuthRoute.value) {
          window.location.replace('/404.html');
          return;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    watch(route, pageLoad)

    return {
      apiError,
      getPathClass,
      pageComponents,
      loading,
      buildNumber,
      isRegisterRoute,
      isInviteRoute,
      isWorkspaceRoute,
      isAuthRoute,
      authPanel,
    }
  },
};
</script>
